import { Color, Spacing } from '@persona-id/design-tokens';
import {
  CursorIcon,
  HandIcon,
  RotateCcwIcon,
  RotateCwIcon,
  SidebarRightIcon,
  ZoomInIcon,
  ZoomOutIcon,
} from '@persona-id/design-tokens/icons';
import { MouseEvent, MouseEventHandler, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';

import { Mode } from 'dashboard/components/GraphCanvasV2/behaviors';
import EventBus from 'dashboard/components/GraphCanvasV2/event-bus';
import CanvasViewportChangeEvent from 'dashboard/components/GraphCanvasV2/events/canvas-viewport-change-event';
import ModeChangedEvent from 'dashboard/components/GraphCanvasV2/events/mode-changed-event';
import G6Wrapper from 'dashboard/components/GraphCanvasV2/G6Wrapper';
import GraphQueryBar from 'dashboard/components/GraphToolbar/GraphQueryBar';
import ZoomInput from 'dashboard/components/GraphToolbar/ZoomInput';
import ReactSelect from 'dashboard/components/ReactSelect';
import Tooltip from 'dashboard/components/Tooltip';
import { GraphExplorerQueryLoadingState, GraphViewTypes } from 'dashboard/models/graph-explorer';
import { GraphQueryBody } from 'dashboard/models/graph-query';
import { GraphQueryTemplate } from 'dashboard/models/graph-query-template';
import { Z_INDICES } from 'dashboard/styles/variables';
import SidebarRightFilledIcon from 'images/icons/graph/sidebar-right-filled.svg';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';

const HIDE_UNDO_AND_REDO = true;

const GraphToolbarContainer = styled.div<{ width: number }>`
  background: white;
  display: flex;
  flex-direction: horizontal;
  border-bottom: 1px solid ${Color.CoreSlate100};
  width: ${({ width }) => `${width}px`};
  position: absolute;
  top: 0;
`;

const StyledTooltip = styled(Tooltip)`
  z-index: ${Z_INDICES.floatingBar};
  position: relative;
`;

const ToolbarList = styled('ul')`
  display: flex;
  flex-direction: row;
  list-style-type: none;
  padding-left: ${Spacing[2]};
  width: 100%;
`;

const RightTools = styled('span')`
  margin-left: auto;
  margin-right: ${Spacing[2]};
`;

const Tool = styled('li')`
  height: 32px;
  border-radius: 3px;
  padding: ${Spacing[2]};
  margin: ${Spacing[2]} 0;
`;

const ToolInputContainer = styled('li')`
  height: 36px;
  padding: ${Spacing[2]};
  margin: 0;
`;

const ToolButton = styled(Tool)<{ active: boolean; disabled: boolean }>`
  width: 32px;

  &:hover {
    background-color: ${Color.CorePurple200};

    path {
      stroke: ${Color.PrimaryPurple};
    }
  }

  ${({ active }) =>
    active
      ? `background-color: ${Color.CorePurple100} !important;`
      : `background-color: ${Color.PrimaryWhite} !important`}

  path {
    stroke: ${({ active }) => (active ? Color.PrimaryPurple : 'currentColor')};
  }

  ${({ disabled }) =>
    disabled
      ? `
      path {
        fill: ${Color.CoreSlate200};
      }

      &:hover {
        cursor: not-allowed;
        background-color: unset;
      }
      `
      : ``}
`;

const StyledIconHand = styled(HandIcon)`
  width: 16px;
  height: 16px;
`;

const StyledIconSelect = styled(CursorIcon)`
  width: 16px;
  height: 16px;
`;

const StyledIconZoomIn = styled(ZoomInIcon)`
  width: 16px;
  height: 16px;
`;

const StyledIconZoomOut = styled(ZoomOutIcon)`
  width: 16px;
  height: 16px;
`;

const StyledIconUndo = styled(RotateCcwIcon)`
  width: 16px;
  height: 16px;
`;

const StyledIconRedo = styled(RotateCwIcon)`
  width: 16px;
  height: 16px;
`;

const StyledIconSidebarRight = styled(SidebarRightIcon)`
  width: 16px;
  height: 16px;
`;

const StyledIconSidebarRightFilled = styled(SidebarRightFilledIcon)`
  width: 16px;
  height: 16px;
`;

const noop = () => {
  return;
};

const VIEW_TYPE_OPTIONS = [
  {
    label: GraphViewTypes.Parsed,
    value: 'Parsed',
  },
  {
    label: GraphViewTypes.Expanded,
    value: 'Expanded',
  },
];

interface GraphToolbarProps {
  canZoomIn: boolean;
  canZoomOut: boolean;
  clearGraphQueryTemplate: () => void;
  eventBus: EventBus;
  g6Wrapper: G6Wrapper;
  graphQueryBarEnabled: boolean;
  graphQueryEditorVisible: boolean;
  graphQueryEditorToggle: () => void;
  graphQueryTemplate: GraphQueryTemplate;
  handleClearGraphQueryAndGraphQueryTemplate: () => void;
  handleClickUndo: MouseEventHandler<HTMLLIElement>;
  handleClickRedo: MouseEventHandler<HTMLLIElement>;
  handleLoadGraphQueryTemplate: (graphQueryTemplateId: string) => Promise<void>;
  handleRightPanelToggleClick: (e: MouseEvent<HTMLLIElement>) => void;
  handleQuery: (query: GraphQueryBody) => void;
  handleSaveGraphQueryTemplate: (
    id: string | null,
    name: string,
    description: string | null,
    payload: GraphQueryBody,
    saveAs: boolean,
  ) => Promise<boolean>;
  handleZoomInClick: () => void;
  handleZoomOutClick: () => void;
  handleZoomPercentageChange: (zoomPercentage: number) => void;
  isDemoBackend: boolean;
  loadingGraphEnvironment: boolean;
  nodeLimit: number;
  queryLoadingState: GraphExplorerQueryLoadingState;
  rightPanelExpanded: boolean;
  width: number;
  zoomPercentage: number;
  selectedViewType: string;
  handleToggleParseView: (value: string) => void;
}

const GraphToolbar = ({
  canZoomIn,
  canZoomOut,
  clearGraphQueryTemplate,
  eventBus,
  g6Wrapper,
  graphQueryBarEnabled,
  graphQueryEditorToggle,
  graphQueryEditorVisible,
  graphQueryTemplate,
  handleClearGraphQueryAndGraphQueryTemplate,
  handleClickRedo,
  handleClickUndo,
  handleLoadGraphQueryTemplate,
  handleRightPanelToggleClick,
  handleQuery,
  handleSaveGraphQueryTemplate,
  handleZoomInClick,
  handleZoomOutClick,
  handleZoomPercentageChange,
  isDemoBackend,
  loadingGraphEnvironment,
  nodeLimit,
  rightPanelExpanded,
  width,
  zoomPercentage,
  handleToggleParseView,
  selectedViewType,
}: GraphToolbarProps) => {
  const showParsedToggle = useFeatureFlag('dashboard:graph-pairwise-similarity-nodes');
  const [currentMode, setCurrentMode] = useState(Mode.SELECT);
  const handleModeChange = useCallback(() => {
    setCurrentMode(eventBus.mode);
  }, [eventBus]);

  useEffect(() => {
    eventBus.addEventListener(ModeChangedEvent.type, handleModeChange);

    return () => {
      eventBus.removeEventListener(ModeChangedEvent.type, handleModeChange);
    };
  }, [eventBus, handleModeChange, setCurrentMode]);

  const handlePanModeButtonClick = () => {
    eventBus.setMode(Mode.PAN);
  };

  const handleSelectModeButtonClick = () => {
    eventBus.setMode(Mode.SELECT);
  };

  return (
    <GraphToolbarContainer width={width}>
      {graphQueryBarEnabled && (
        <GraphQueryBar
          clearGraphQueryTemplate={clearGraphQueryTemplate}
          graphQueryEditorToggle={graphQueryEditorToggle}
          graphQueryEditorVisible={graphQueryEditorVisible}
          graphQueryTemplate={graphQueryTemplate}
          handleClearGraphQueryAndGraphQueryTemplate={handleClearGraphQueryAndGraphQueryTemplate}
          handleLoadGraphQueryTemplate={handleLoadGraphQueryTemplate}
          handleQuery={handleQuery}
          handleSaveGraphQueryTemplate={handleSaveGraphQueryTemplate}
          loadingGraphEnvironment={loadingGraphEnvironment}
          nodeLimit={nodeLimit}
          width={width}
        />
      )}
      <ToolbarList style={{ borderWidth: 0 }}>
        <StyledTooltip placement='top' trigger='hover' tooltip='Select mode' hideArrow={true}>
          <ToolButton
            id='graph_explorer__toolbar-button-select'
            active={currentMode === Mode.SELECT}
            disabled={false}
            onClick={handleSelectModeButtonClick}
          >
            <StyledIconSelect />
          </ToolButton>
        </StyledTooltip>
        <StyledTooltip placement='top' trigger='hover' tooltip='Pan mode' hideArrow={true}>
          <ToolButton
            id='graph_explorer__toolbar-button-pan'
            active={currentMode === Mode.PAN}
            disabled={false}
            onClick={handlePanModeButtonClick}
          >
            <StyledIconHand />
          </ToolButton>
        </StyledTooltip>
        {!HIDE_UNDO_AND_REDO && (
          <>
            <StyledTooltip placement='top' trigger='hover' tooltip='Undo' hideArrow={true}>
              <ToolButton
                id='graph_explorer__toolbar-button-undo'
                active={false}
                disabled={false}
                onClick={handleClickUndo}
              >
                <StyledIconUndo />
              </ToolButton>
            </StyledTooltip>
            <StyledTooltip placement='top' trigger='hover' tooltip='Redo' hideArrow={true}>
              <ToolButton
                id='graph_explorer__toolbar-button-redo'
                active={false}
                disabled={false}
                onClick={handleClickRedo}
              >
                <StyledIconRedo />
              </ToolButton>
            </StyledTooltip>
          </>
        )}
        <StyledTooltip placement='top' trigger='hover' tooltip='Zoom in' hideArrow={true}>
          <ToolButton
            id='graph_explorer__toolbar-button-zoom-in'
            active={false}
            disabled={!canZoomIn}
            onClick={canZoomIn ? handleZoomInClick : noop}
          >
            <StyledIconZoomIn />
          </ToolButton>
        </StyledTooltip>
        <StyledTooltip placement='top' trigger='hover' tooltip='Zoom out' hideArrow={true}>
          <ToolButton
            id='graph_explorer__toolbar-button-zoom-out'
            active={false}
            disabled={!canZoomOut}
            onClick={canZoomOut ? handleZoomOutClick : noop}
          >
            <StyledIconZoomOut />
          </ToolButton>
        </StyledTooltip>
        <ToolInputContainer id='graph_explorer__toolbar-input-zoom'>
          <ZoomInput
            zoomPercentage={zoomPercentage}
            handleZoomPercentageChange={handleZoomPercentageChange}
          />
        </ToolInputContainer>
        {showParsedToggle && (
          <ToolInputContainer id='graph_explorer__toolbar-input-parse-toggle'>
            <ReactSelect
              placeholder={`Layout: ${selectedViewType}`}
              value={`Layout: ${selectedViewType}`}
              onChange={handleToggleParseView}
              options={VIEW_TYPE_OPTIONS}
              portal
            />
          </ToolInputContainer>
        )}
        <RightTools>
          <StyledTooltip
            placement='top'
            trigger='hover'
            tooltip={rightPanelExpanded ? 'Collapse panel' : 'Expand panel'}
            hideArrow={true}
          >
            <ToolButton
              id='graph_explorer__toolbar-button-right-panel-toggle'
              active={false}
              disabled={false}
              onClick={(e) => {
                handleRightPanelToggleClick(e);
                eventBus.dispatchEvent(
                  new CanvasViewportChangeEvent(e as unknown as Event, g6Wrapper.canvas),
                );
              }}
            >
              {isDemoBackend ? <StyledIconSidebarRightFilled /> : <StyledIconSidebarRight />}
            </ToolButton>
          </StyledTooltip>
        </RightTools>
      </ToolbarList>
    </GraphToolbarContainer>
  );
};

export default GraphToolbar;
